import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { errorMessage } from '../../api/client';
import { fetchFixedInvestmentImageUrl, useAddFixedInvestment, useFixedAccount } from '../../api/hooks';
import type { FixedInvestment, PartyType } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import PendingDocumentPicker from '../investor/PendingDocumentPicker';
import TransactionImageLink from '../investor/TransactionImageLink';
import TransactionAccountField from './TransactionAccountField';
import { useTransactionAccountField } from '../../utils/transactionAccountField';
import QuickShareButton from './QuickShareButton';
import { usePartyContact } from './usePartyContact';
import { transactionMessage } from '../../utils/quickShare';
import { useTranslation } from 'react-i18next';
import AutoText from './AutoText';

interface Props {
  partyType: PartyType;
  partyId: number;
  canWrite: boolean;
}

/** Countdown tag for one investment's return date. */
function DueTag({ days, overdue, settled }: { days: number; overdue: boolean; settled: boolean }) {
  if (settled) return <Tag color="default">Settled</Tag>;
  if (overdue) return <Tag color="red">{Math.abs(days)} days overdue</Tag>;
  return <Tag color={days <= 7 ? 'orange' : days <= 30 ? 'gold' : 'blue'}>{days} days left</Tag>;
}

/**
 * Fixed-return investments for one account: capital in, an agreed total back, drawn down by
 * withdrawals. One component serves both books — the arithmetic is identical and only the wording
 * differs — and it shares nothing with the percentage model, which is untouched.
 */
export default function FixedInvestmentsTab({ partyType, partyId, canWrite }: Props) {
  const { t } = useTranslation();
  const { data: account, isLoading, error } = useFixedAccount(partyType, partyId);
  const addInvestment = useAddFixedInvestment(partyType, partyId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const accountField = useTransactionAccountField(partyType, partyId);
  const contact = usePartyContact(partyType, partyId, 'FixedReturn');

  // Watched so the agreed profit can be shown live while the two amounts are typed.
  const amount = Form.useWatch('amount', form) as number | undefined;
  const returnAmount = Form.useWatch('returnAmount', form) as number | undefined;
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !account) return <Skeleton active paragraph={{ rows: 6 }} />;

  // The date agreed when the account was opened, reused as the default for later entries.
  const defaultDueDate = account.investments.length > 0 ? dayjs(account.investments[0].dueDate) : dayjs();

  const profit = (returnAmount ?? 0) - (amount ?? 0);

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addInvestment.mutateAsync({
        amount: values.amount as number,
        returnAmount: values.returnAmount as number,
        investDate: apiDate(values.investDate as dayjs.Dayjs)!,
        dueDate: apiDate(values.dueDate as dayjs.Dayjs)!,
        paymentMethod: values.paymentMethod as string | undefined,
        notes: values.notes as string | undefined,
        transactionImage,
      });
      await accountField.commitIfChanged();
      message.success('Investment added successfully.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ investDate: dayjs(), dueDate: defaultDueDate });
              setOpen(true);
            }}
          >
            Add investment
          </Button>
        </Space>
      )}

      {account.hasOverdue && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="One or more investments are past their return date with money still owed."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title={t('finance.totalInvestment')} value={money(account.totalInvested)} prefix="PKR" valueStyle={{ fontSize: 20 }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Capital handed over
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('finance.totalReturnAgreed')}
              value={money(account.totalReturnAgreed)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#FE9F43' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Profit {money(account.totalProfit)}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title={t('finance.totalWithdrawn')} value={money(account.totalWithdrawn)} prefix="PKR" valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            {/* Capital still with the business, on the same definition the percentage book uses:
                what was paid in, less what has been paid back. Distinct from the payable balance
                beside it, which is measured against the agreed return rather than the capital. */}
            <Statistic
              title={t('finance.totalInvestmentRemaining')}
              value={money(Math.max(0, account.totalInvested - account.totalWithdrawn))}
              prefix="PKR"
              valueStyle={{ fontSize: 20 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Capital still placed
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Total payable amount"
              value={money(account.totalRemaining)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: account.totalRemaining > 0 ? '#FF0000' : '#3EB780' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {account.nextDueDate ? `Next due ${date(account.nextDueDate)}` : 'Nothing outstanding'}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Table<FixedInvestment>
        rowKey="id"
        style={{ marginTop: 16 }}
        dataSource={account.investments}
        scroll={{ x: 'max-content' }}
        pagination={false}
        rowClassName={(row) => (row.isOverdue ? 'overdue-row' : '')}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: t('common.date'), dataIndex: 'investDate', render: date },
          { title: 'Investment', dataIndex: 'amount', align: 'right', render: money },
          { title: 'Return Agreed Amount', dataIndex: 'returnAmount', align: 'right', render: money },
          { title: 'Profit Amount', dataIndex: 'profitAmount', align: 'right', render: money },
          { title: t('finance.withdrawn'), dataIndex: 'withdrawnAmount', align: 'right', render: money },
          {
            title: t('finance.payableRemaining'),
            dataIndex: 'remainingAmount',
            align: 'right',
            render: (v: number) => <strong>{money(v)}</strong>,
          },
          { title: 'Return date', dataIndex: 'dueDate', render: date },
          {
            title: t('common.status'),
            render: (_, row) => (
              <DueTag days={row.daysRemaining} overdue={row.isOverdue} settled={row.remainingAmount <= 0} />
            ),
          },
          { title: t('finance.paymentMethod'), dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
          { title: t('common.notes'), dataIndex: 'notes', render: (v: string | null) => <AutoText value={v} /> },
          {
            title: t('finance.transactionProof'),
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — investment #${row.id}`}
                fetchUrl={() => fetchFixedInvestmentImageUrl(partyType, partyId, row.id)}
              />
            ),
          },
          ...(contact.ready
            ? [
                {
                  title: t('common.share'),
                  render: (_: unknown, row: FixedInvestment) => (
                    <QuickShareButton
                      phone={contact.phone}
                      recipientName={contact.name}
                      message={transactionMessage(contact, 'New investment', row.amount, row.investDate)}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal maskClosable={false}
        title="Add investment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addInvestment.isPending}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item
            name="amount"
            label="Total investment (PKR)"
            rules={[{ required: true }, { type: 'number', min: 0.01 }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="returnAmount"
            label={t('forms.fixedReturnAmount')}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator: (_, value: number) =>
                  value === undefined || value === null || value >= (getFieldValue('amount') ?? 0)
                    ? Promise.resolve()
                    : Promise.reject(new Error('The return cannot be less than the amount invested.')),
              }),
            ]}
            help="The whole sum coming back, capital included."
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>

          {(amount ?? 0) > 0 && (returnAmount ?? 0) > 0 && (
            <Alert
              type={profit < 0 ? 'error' : 'success'}
              showIcon
              style={{ marginBottom: 16, marginTop: -8 }}
              message={
                profit < 0
                  ? `The return is ${money(Math.abs(profit))} less than the amount invested.`
                  : `Profit ${money(profit)} on ${money(amount!)} invested.`
              }
            />
          )}

          <Form.Item name="investDate" label={t('finance.investmentDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item
            name="dueDate"
            label={t('forms.returnDueOn')}
            rules={[{ required: true }]}
            help="Pre-filled from the date agreed when the account was opened. Change it if this one differs."
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="paymentMethod" label={t('finance.paymentMethod')}>
            <Select placeholder="How the money moved" allowClear options={paymentMethodOptions} />
          </Form.Item>
          <TransactionAccountField field={accountField} paymentMethod={paymentMethod} />
          <Form.Item name="notes" label={t('common.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="transaction-image"
            label={t('finance.transactionProofOptional')}
            hint="Bank slip, wallet screenshot or cash voucher · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
